import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch } from "../../store";
import { useGetRole } from "./getRole";
import USER_ROLES from "../../types/UserRoles";
import { setSystemState } from "../../acions/actionsCreators/systemState";
import authAxios, { authHeader } from "../../services/data";

const SSE_RECONNECT_DELAY_MS = 3000;

export const useServerSideEvents = () => {
  const [role, isAdmin, data] = useGetRole();

  const dispatch = useAppDispatch();
  const sourceRef = useRef<EventSource | null>(null);
  const connectRef = useRef<() => Promise<void>>();
  const reconnectTimeoutRef = useRef<number | null>(null);

  const shouldConnectSse = useMemo(
    () => !!data && isAdmin && role !== USER_ROLES.INTEGRATION_RECEPTIONIST,
    [data, isAdmin, role]
  );

  const getSseToken = useCallback(async () => {
    try {
      const response = await authAxios.get("/api/sse/admin/token", {
        headers: authHeader()
      });
      return response.data;
    } catch {}
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    sourceRef.current?.close();
    sourceRef.current = null;
  }, [clearReconnectTimeout]);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimeout();
    reconnectTimeoutRef.current = window.setTimeout(() => {
      void connectRef.current?.();
    }, SSE_RECONNECT_DELAY_MS);
  }, [clearReconnectTimeout]);

  const connect = useCallback(async () => {
    disconnect();

    try {
      const token = await getSseToken();
      const source = new EventSource(`/api/sse/admin/notifications?token=${encodeURIComponent(token)}`);

      sourceRef.current = source;

      source.addEventListener("system_state", (e) => {
        const nextState = JSON.parse((e as MessageEvent).data);
        dispatch(setSystemState(nextState));
      });

      source.onerror = () => {
        source.close();

        if (sourceRef.current === source) {
          sourceRef.current = null;
        }

        scheduleReconnect();
      };

      source.onopen = () => {
        clearReconnectTimeout();
      };
    } catch {
      scheduleReconnect();
    }
  }, [clearReconnectTimeout, disconnect, dispatch, getSseToken, scheduleReconnect]);

  connectRef.current = connect;

  useEffect(() => {
    if (!shouldConnectSse) {
      disconnect();
      return;
    }

    void connect();

    return () => {
      disconnect();
    };
  }, [shouldConnectSse, connect, disconnect]);
};
